'use client';

import SmartLink from '@/components/SmartLink';
import TimeLocal from '@/components/TimeLocal';
import { useGameStatusAndScoresUpdates } from '@/hooks/useGameUpdates';
import { periodLabel } from '@/lib/liveStats';
import { cn, getClockFromMilliseconds } from '@/lib/utils';
import { OtnGame } from '@models/ot_nationals/otn_game';
import { imgPartnersEspn2LogoSvg } from '@public';
import { Urls } from '@urls';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

// TODO: add stream link to game model
const gameStreamUrl = 'https://www.espn.com/watch/player/_/id/630a079f-809e-44bc-a488-50119567e4aa';

// TODO: refactor variable naming convention
const Game = ({ game: otnGame }: { game: OtnGame }) => {
	const { game, gameTeamHomeStats, gameTeamAwayStats } = useGameStatusAndScoresUpdates(otnGame);
	const pathname = usePathname();
	const isOnGamePage = pathname === `/games/${game.id}`;

	//TODO:  create end game state
	const homeTeamScore = gameTeamHomeStats?.score ?? 0;
	const awayTeamScore = gameTeamAwayStats?.score ?? 0;

	// Games driven by the in-house stat tool carry game_phase; the broadcast owns the clock, so
	// they show the period plus halftime / timeout instead. Everything else keeps the original
	// clock-based display exactly as before.
	const isLiveStatsGame = game.game_phase != null;

	const gameIsComplete = isLiveStatsGame ? game.is_complete || game.game_phase === 'final' : game.is_complete;
	const gameClock = getClockFromMilliseconds(game.game_clock);
	// A new game has period 0 and a zero clock until someone scores it; show its kickoff time until then.
	const gameHasStarted = isLiveStatsGame
		? !gameIsComplete && (game.game_phase === 'in_progress' || game.game_phase === 'halftime')
		: !gameIsComplete && ((game.period ?? 0) > 0 || (game.game_clock ?? 0) > 0);
	const period = isLiveStatsGame
		? periodLabel(game.period)
		: game.period && game.period > 0
			? game.period <= 4
				? `Q${game.period}`
				: `OT${game.period - 4}`
			: '';
	const timeoutTeam = game.is_timeout
		? game.otn_games_otn_teams?.find((t) => t.otn_team_id === game.timeout_otn_team_id)?.otn_team
		: undefined;

	const teamContainerClass = 'flex flex-1 items-center ';
	const teamLinkClass = 'flex items-center gap-3 sm:gap-4';
	const teamLogoClass = 'relative h-14 w-14';
	const teamNameClass = 'hidden md:inline-block';
	const scoreClass = 'text-center font-beachwood text-[28px] font-bold leading-[120%]';

	return (
		<section className={cn('otn-section-border bg-black', !isOnGamePage && 'grid')}>
			{/* Background link - covers entire card, clickable to game details (only when not on game page) */}
			{!isOnGamePage && (
				<SmartLink
					href={`/games/${game.id}`}
					className="z-0 col-start-1 row-start-1"
					aria-label={`View ${game.competition_name} game details`}
				>
					<span className="sr-only">View game details</span>
				</SmartLink>
			)}

			{/* Content layer - sits on top of background link */}
			<div
				className={cn(
					'flex w-full flex-col items-center p-4 md:py-6',
					!isOnGamePage && 'pointer-events-none z-10 col-start-1 row-start-1',
				)}
			>
				<h2 className="text-otnBlue font-beachwood text-center text-[13px] leading-[120%] font-extrabold tracking-[1.95px] uppercase">
					{game.competition_name}
				</h2>
				<div className="flex w-full items-center gap-2 md:gap-6">
					<div className={cn(teamContainerClass, 'justify-end')}>
						<SmartLink
							href={`/teams/${gameTeamHomeStats?.otn_team?.slug || '#'}`}
							className={cn(teamLinkClass, !isOnGamePage && 'pointer-events-auto')}
							aria-label={`View ${gameTeamHomeStats?.otn_team?.school_name || 'home team'} details`}
						>
							<p className={teamNameClass}>{gameTeamHomeStats?.otn_team?.school_name}</p>
							<div className={teamLogoClass}>
								<Image
									src={`${Urls.OvertimeImagesBaseUrl}${gameTeamHomeStats?.otn_team?.logo_path}`}
									fill
									alt={`${gameTeamHomeStats?.otn_team?.name || 'Home team'} logo`}
								/>
							</div>
							{(gameHasStarted || gameIsComplete) && <div className={scoreClass}>{homeTeamScore}</div>}
						</SmartLink>
					</div>

					{gameIsComplete ? (
						<div className="my-1 space-y-2 px-3 text-center">
							<div className="uppercase">Final</div>
						</div>
					) : gameHasStarted && isLiveStatsGame ? (
						<div className="my-1 space-y-2 text-center" data-testid="game-live-status">
							<div className="leading-[150%] uppercase">{game.game_phase === 'halftime' ? 'Half' : period}</div>
							{game.is_timeout && (
								<div className="text-otnLime text-[11px] leading-[150%] font-extrabold uppercase">
									Timeout{timeoutTeam?.team_code ? ` · ${timeoutTeam.team_code}` : ''}
								</div>
							)}
						</div>
					) : gameHasStarted ? (
						<div className="my-1 space-y-2 text-center">
							<div className="leading-[150%]">{period}</div>
							<div className="leading-[150%]">
								<div>{gameClock}</div>
							</div>
						</div>
					) : (
						<TimeLocal
							className="text-center text-[16px] leading-[150%] font-normal uppercase"
							timestamp={game.starts_at}
							options={{
								weekday: 'short',
								month: 'short',
								day: 'numeric',
								time: true,
								timeZoneName: true,
								prependAt: true,
							}}
						/>
					)}
					<div className={teamContainerClass}>
						<SmartLink
							href={`/teams/${gameTeamAwayStats?.otn_team?.slug || '#'}`}
							className={cn(teamLinkClass, !isOnGamePage && 'pointer-events-auto')}
							aria-label={`View ${gameTeamAwayStats?.otn_team?.school_name || 'away team'} details`}
						>
							{(gameHasStarted || gameIsComplete) && <div className={scoreClass}>{awayTeamScore}</div>}
							<div className={teamLogoClass}>
								<Image
									src={`${Urls.OvertimeImagesBaseUrl}${gameTeamAwayStats?.otn_team?.logo_path}`}
									fill
									alt={`${gameTeamAwayStats?.otn_team?.name || 'Away team'} logo`}
								/>
							</div>
							<p className={teamNameClass}>{gameTeamAwayStats?.otn_team?.school_name}</p>
						</SmartLink>
					</div>
				</div>
				<SmartLink
					className={cn('text-otnGray flex items-center', !isOnGamePage && 'pointer-events-auto')}
					href={gameStreamUrl}
				>
					<Image src={imgPartnersEspn2LogoSvg} alt="ESPN E logo" className="h-[12px] w-auto" />
				</SmartLink>
			</div>
		</section>
	);
};

export default Game;
